import React, { useState, useCallback } from 'react';
import { X, ArrowLeft, ArrowRight, Loader2, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

type InputType = 'excellence' | 'failure' | 'both';

interface CustomReviewOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (rubric: {
    name: string;
    inputType: InputType;
    excellenceInput?: string;
    failureInput?: string;
    descriptions: {
      A: string;
      B: string;
      C: string;
      D: string;
      F: string;
    };
  }) => Promise<void>;
}

type WizardStep = 1 | 2 | 3 | 4;


export const CustomReviewOverlay: React.FC<CustomReviewOverlayProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const [step, setStep] = useState<WizardStep>(1);
  const [inputType, setInputType] = useState<InputType | null>(null);
  const [excellenceInput, setExcellenceInput] = useState('');
  const [failureInput, setFailureInput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [descriptions, setDescriptions] = useState<{
    A: string;
    B: string;
    C: string;
    D: string;
    F: string;
  }>({
    A: '',
    B: '',
    C: '',
    D: '',
    F: '',
  });
  const [rubricName, setRubricName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Reset wizard state
  const resetWizard = useCallback(() => {
    setStep(1);
    setInputType(null);
    setExcellenceInput('');
    setFailureInput('');
    setDescriptions({ A: '', B: '', C: '', D: '', F: '' });
    setRubricName('');
    setGenerationError(null);
  }, []);

  const handleClose = () => {
    resetWizard();
    onClose();
  };

  const handleSelectInputType = (type: InputType) => {
    setInputType(type);
    setStep(2);
  };

  const handleGenerateDescriptions = async () => {
    if (!inputType) return;

    setIsGenerating(true);
    setGenerationError(null);

    try {
      const generateCustomRubric = httpsCallable(functions, 'generateCustomRubric');
      const result = await generateCustomRubric({
        inputType,
        excellenceInput: inputType !== 'failure' ? excellenceInput : undefined,
        failureInput: inputType !== 'excellence' ? failureInput : undefined,
      });

      const data = result.data as {
        descriptions: { A: string; B: string; C: string; D: string; F: string };
      };

      setDescriptions(data.descriptions);
      setStep(3);
    } catch (error: any) {
      console.error('❌ Error generating rubric:', error);
      setGenerationError(error.message || 'Failed to generate rubric descriptions');
    } finally {
      setIsGenerating(false);
    }
  };

  const canProceedFromStep2 = () => {
    if (inputType === 'excellence') return excellenceInput.trim().length > 0;
    if (inputType === 'failure') return failureInput.trim().length > 0;
    if (inputType === 'both') return excellenceInput.trim().length > 0 && failureInput.trim().length > 0;
    return false;
  };

  const canSave = () => {
    return (
      descriptions.A.trim() &&
      descriptions.B.trim() &&
      descriptions.C.trim() &&
      descriptions.D.trim() &&
      descriptions.F.trim() &&
      rubricName.trim()
    );
  };

  const handleSave = async () => {
    console.log('🔵 handleSave called', { canSave: canSave(), inputType, rubricName, descriptions });
    
    if (!canSave()) {
      console.log('❌ canSave() returned false');
      return;
    }
    if (!inputType) {
      console.log('❌ inputType is null');
      return;
    }

    setIsSaving(true);
    setGenerationError(null);
    
    try {
      console.log('🔵 Calling onSave...');
      await onSave({
        name: rubricName.trim(),
        inputType,
        excellenceInput: inputType !== 'failure' ? excellenceInput : undefined,
        failureInput: inputType !== 'excellence' ? failureInput : undefined,
        descriptions,
      });
      console.log('✅ onSave completed successfully');
      handleClose();
    } catch (error: any) {
      console.error('❌ Error saving rubric:', error);
      setGenerationError(error.message || 'Failed to save rubric');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDescriptionChange = (grade: 'A' | 'B' | 'C' | 'D' | 'F', value: string) => {
    setDescriptions(prev => ({ ...prev, [grade]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-background border border-border rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">Custom Review Rubric</h2>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4].map((s) => (
                <div
                  key={s}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    s === step
                      ? 'bg-primary'
                      : s < step
                      ? 'bg-primary/50'
                      : 'bg-muted'
                  }`}
                />
              ))}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-muted rounded-md transition-colors"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Choose input type */}
          {step === 1 && (
            <div className="space-y-6">
              <p className="text-sm text-muted-foreground text-center">
                Would you like to provide a custom definition of what excellence looks like,
                what failure looks like, or both?
              </p>

              <div className="flex flex-col gap-3">
                <button
                  onClick={() => handleSelectInputType('excellence')}
                  className="w-full px-4 py-3 border border-border rounded-lg hover:bg-muted/50 hover:border-primary/50 transition-all text-left"
                >
                  <div className="font-medium">Excellence</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Define what an excellent AI response looks like
                  </div>
                </button>

                <button
                  onClick={() => handleSelectInputType('failure')}
                  className="w-full px-4 py-3 border border-border rounded-lg hover:bg-muted/50 hover:border-primary/50 transition-all text-left"
                >
                  <div className="font-medium">Failure</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Define what a failing AI response looks like
                  </div>
                </button>

                <button
                  onClick={() => handleSelectInputType('both')}
                  className="w-full px-4 py-3 border border-border rounded-lg hover:bg-muted/50 hover:border-primary/50 transition-all text-left"
                >
                  <div className="font-medium">Both</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Define both excellence and failure for the clearest rubric
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Input definitions */}
          {step === 2 && inputType && (
            <div className="space-y-6">
              {(inputType === 'excellence' || inputType === 'both') && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium">
                    Define what excellence means to you in an AI chatbot response
                  </label>
                  <textarea
                    value={excellenceInput}
                    onChange={(e) => setExcellenceInput(e.target.value)}
                    placeholder="e.g., A response that provides accurate, well-structured code with clear explanations and considers edge cases..."
                    className="w-full h-32 px-3 py-2 text-sm bg-muted border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              )}

              {(inputType === 'failure' || inputType === 'both') && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium">
                    Define what failure means to you in an AI chatbot response
                  </label>
                  <textarea
                    value={failureInput}
                    onChange={(e) => setFailureInput(e.target.value)}
                    placeholder="e.g., A response that contains factual errors, ignores the question, or provides dangerous advice..."
                    className="w-full h-32 px-3 py-2 text-sm bg-muted border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              )}

              {generationError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-500">
                  {generationError}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Review and edit generated descriptions */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground text-center mb-4">
                Review and edit the generated descriptions. All five must be filled.
              </p>

              <div className="flex gap-3">
                {/* Gradient bar with labels */}
                <div className="flex flex-col items-center w-6 shrink-0">
                  <span className="text-[10px] font-medium text-emerald-500 mb-1">Excellence</span>
                  <div className="flex-1 w-1.5 rounded-full bg-gradient-to-b from-emerald-500 via-amber-500 to-red-500 min-h-[280px]" />
                  <span className="text-[10px] font-medium text-red-500 mt-1">Failure</span>
                </div>

                {/* Description text areas */}
                <div className="flex-1 space-y-3">
                  {(['A', 'B', 'C', 'D', 'F'] as const).map((grade, index) => (
                    <div key={grade}>
                      <textarea
                        value={descriptions[grade]}
                        onChange={(e) => handleDescriptionChange(grade, e.target.value)}
                        placeholder={index === 0 ? 'Excellence...' : index === 4 ? 'Failure...' : ''}
                        className="w-full h-20 px-3 py-2 text-xs bg-muted border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Name and save */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium">
                  Name your rubric
                </label>
                <input
                  type="text"
                  value={rubricName}
                  onChange={(e) => setRubricName(e.target.value)}
                  placeholder="e.g., Code Review, Creative Writing, Research Assistant..."
                  className="w-full px-3 py-2 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  autoFocus
                />
              </div>

              <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                <div className="text-xs font-medium text-muted-foreground">Preview</div>
                {(['A', 'B', 'C', 'D', 'F'] as const).map((grade) => (
                  <div key={grade} className="flex gap-2 text-xs">
                    <span className="font-bold w-4">{grade}:</span>
                    <span className="text-muted-foreground line-clamp-1">
                      {descriptions[grade]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-muted/30">
          <div>
            {step > 1 && step !== 3 && !isGenerating && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStep((s) => (s - 1) as WizardStep)}
                className="gap-1"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            )}
            {step === 3 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStep(2)}
                className="gap-1"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {step === 2 && (
              <Button
                onClick={handleGenerateDescriptions}
                disabled={!canProceedFromStep2() || isGenerating}
                size="sm"
                className="gap-1"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    Generate
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            )}

            {step === 3 && (
              <Button
                onClick={() => setStep(4)}
                disabled={!Object.values(descriptions).every(d => d.trim())}
                size="sm"
                className="gap-1"
              >
                Continue
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}

            {step === 4 && (
              <Button
                onClick={handleSave}
                disabled={!canSave() || isSaving}
                size="sm"
                className="gap-1"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    Save & Activate
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

